/**
 * Side rail dimensions, surfaces and motion: the single source for every rail value. Each value is
 * the handoff's ("Navigation and header", sections 2 to 7), copied, not derived.
 *
 * ⚠️ ONE OPEN WIDTH. The panel is 248px open and 72px closed, and the layout slot is 72px: the open
 * panel overlays the page and never shifts it.
 *
 * ⚠️ THE PANEL GRADIENT USES THE LITERAL STOPS, not `var(--hd, #00509a)`. `--hd` is defined
 * (#004080), so a fallback would never apply and the 46% and 100% stops would both render #004080.
 */
const EASE = {
  standard: 'cubic-bezier(0.4, 0, 0.2, 1)',
  emphasised: 'cubic-bezier(0.32, 0.72, 0, 1)',
} as const;

export const RAIL = {
  collapsedWidth: 72,
  openWidth: 248,
  brandHeight: 72,
  rowHeight: 44,
  slotZIndex: 25,
  easing: EASE,
  // The slot (section 2 "Layout slot"). It is constant at 72px outside a project and moves only
  // between 72 and 10 when a project route is entered or left.
  slotTransition: `width 0.24s ${EASE.standard}`,
  // The panel (section 2 "Panel"), outside a project.
  panelTransition: `transform 0.35s ${EASE.emphasised}, width 0.24s ${EASE.emphasised}, box-shadow 0.35s`,
  fadeTransition: `opacity 0.18s ease, max-width 0.24s ${EASE.standard}`,
  panelBackground: [
    'radial-gradient(120% 55% at 0% 0%, rgba(96,176,244,0.34) 0%, rgba(96,176,244,0) 62%)',
    'radial-gradient(90% 38% at 100% 100%, rgba(255,198,39,0.10) 0%, rgba(255,198,39,0) 70%)',
    'linear-gradient(170deg,#0067b8 0%,#00509a 46%,#003a73 100%)',
  ].join(', '),
  panelBorder: '1px solid rgba(255,255,255,0.12)',
  shadowDocked: '0 4px 16px rgba(0,103,184,0.25), 0 14px 40px rgba(0,40,90,0.28)',
  shadowOverlay: '0 4px 16px rgba(0,103,184,0.25), 0 24px 48px rgba(0,30,70,0.38)',
  hairline: 'linear-gradient(90deg, rgba(255,255,255,0), rgba(255,255,255,0.24), rgba(255,255,255,0))',
  // Rows (sections 4 and 5).
  rowTransition: `background 0.2s ${EASE.standard}, color 0.2s`,
  idle: 'rgba(255,255,255,0.9)',
  hoverFill: 'rgba(255,255,255,0.09)',
  activeFill: 'linear-gradient(90deg, rgba(255,255,255,0.2), rgba(255,255,255,0.08))',
  activeFillHover: 'linear-gradient(90deg, rgba(255,255,255,0.22), rgba(255,255,255,0.1))',
  markShadow: '0 0 10px rgba(255,198,39,0.65)',
  // The model level (section 5): a name caption over 40px links on a 44px pitch, a 1px thread and
  // one gold segment that slides to the active link.
  modelLinks: {
    linkHeight: 40,
    linkPitch: 44,
    openMs: 400,
    captionMs: 240,
    idle: 'rgba(255,255,255,0.82)',
    hoverFill: 'rgba(255,255,255,0.08)',
    nameColor: 'rgba(255,255,255,0.7)',
    thread: 'rgba(255,255,255,0.18)',
    segmentHeight: 24,
  },
  // Project mode (section 7): the slot is a 10px strip and the 72px panel waits 62px off-screen, so
  // 10px of it shows under the strip.
  project: {
    slotWidth: 10,
    closedTranslateX: -62,
    shadowClosed: '0 2px 8px rgba(0,60,120,0.25)',
    openTransition: [
      'transform 0.2s cubic-bezier(0.3, 0, 0.2, 1)',
      'box-shadow 0.2s cubic-bezier(0.3, 0, 0.2, 1)',
      'width 0.2s cubic-bezier(0.3, 0, 0.2, 1)',
    ].join(', '),
    closeTransition: [
      `transform 0.24s ${EASE.standard}`,
      'box-shadow 0.24s ease',
      `width 0.24s ${EASE.standard}`,
    ].join(', '),
    // The gold tick is a background layer: 3px wide, 40px tall, 3px from the left, centred.
    stripBackground: 'linear-gradient(#ffc627,#ffc627) 3px 50% / 3px 40px no-repeat',
    // Out fast while the rail opens; back in once the close has finished (.24s).
    stripOpenTransition: 'opacity 0.1s',
    stripCloseTransition: 'opacity 0.2s ease 0.24s',
  },
  // The account block (section 6).
  account: {
    openFill: 'rgba(255,255,255,0.08)',
    hoverFill: 'rgba(255,255,255,0.14)',
    muted: 'rgba(255,255,255,0.7)',
    avatarShadow: '0 2px 8px rgba(255,198,39,0.3)',
    menuGap: 8,
    menuBottom: 12,
  },
  // The tooltip beside a closed row (section 4).
  tooltip: {
    border: '1px solid #ededed',
    shadow: '0 1px 3px rgba(0,0,0,0.04), 0 8px 24px rgba(0,30,70,0.16)',
    gap: 16,
  },
} as const;
